import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { GET_ALL_PROGRAMS } from '@frontend/app/queries';

import {
  Row, Col, Space,
} from '@revfluence/fresh';
import { STAPaymentApp } from '@affiliates/containers';
import { STAPaymentAppContextProvider } from '@affiliates/contexts';
import { DateFilter, IDateRangeSettings } from '@frontend/app/components';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { AppHeader } from '@frontend/app/refresh-components/AppHeader';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import HeaderButton from '@frontend/app/refresh-components/HeaderButton';
import { SlidersIcon } from '@revfluence/fresh-icons/regular/esm';
import { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import { logger } from '@common';
import { OfferDetailCard } from './components/OfferDetailCard';
import { RefreshOfferDefaultImage } from '../OfferImage/RefreshOfferDefaultImage';
import NeedHelpDropdown from '../OfferForm/utils/NeedHelpDropdown';
import { TOfferMember } from '../MemberTable/types';
import { useSTAPaymentAppContext } from '../../contexts/STAPaymentAppContext';
import { RefreshMemberTableFilter } from '../MemberTable/RefreshMemberTableFilter';
import { RefreshOfferFiltersDrawer } from '../OfferTable/RefreshOfferFiltersDrawer';
import { OFFER_STATUS_TYPE, STATS_OFFER_TYPE } from '../../types/globalTypes';
import { useApolloClientForAspirex } from '../../hooks/useApolloClientForAspirex';
import { GetAllBoostData_boostDataForAllAffiliatesInOffer } from '../../queries/types/GetAllBoostData';
import { OfferSettingsCustomTitle } from './components/OfferSettingsCustomTitle/OfferSettingsCustomTitle';
import { getOfferIdFromUrl } from '../../utils/constant';

const { useCallback, useMemo } = React;

interface IProps {
  baseUri: string;
  clientId: string;
  clientName: string;
  dateRangeSettings: IDateRangeSettings;
  onClickDashboardLink: () => void;
  deletedMembers?: readonly TOfferMember[];
  isMemberTableFilterOpen: boolean;
  setIsMemberTableFilterOpen: (open: boolean) => void;
  selectedPayoutIds: number[];
  setSelectedPayoutIds: (ids: number[]) => void;
  selectedLastPayoutIds?: number[];
  setSelectedLastPayoutIds?: (ids: number[]) => void;
  selectedFilters: OFFER_STATUS_TYPE[];
  setSelectedFilters: (filters: OFFER_STATUS_TYPE[]) => void;
  selectedOfferType: STATS_OFFER_TYPE;
  setSelectedOfferType: (type: STATS_OFFER_TYPE) => void;
  selectedPrograms: number[];
  setSelectedPrograms: (programs: number[]) => void;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  numericFilters: Record<string, string[]>;
  setNumericFilters: (filters: Record<string, string[]>) => void;
  selectedStatusFilters: string[];
  setSelectedStatusFilters: (filters: string[]) => void;
  selectedBoostTypes: string[];
  setSelectedBoostTypes: (types: string[]) => void;
  selectedBoostStatuses: string[];
  setSelectedBoostStatuses: (statuses: string[]) => void;
  offerNumericFilters: Record<string, string[]>;
  setOfferNumericFilters: (filters: Record<string, string[]>) => void;
  offerSelectedStatusFilters: string[];
  setOfferSelectedStatusFilters: (filters: string[]) => void;
  boostData?: GetAllBoostData_boostDataForAllAffiliatesInOffer[];
}

interface IRouteParams {
  offerId?: string;
}

enum STA_TITLE {
  DASHBOARD = 'Sales Tracking',
  NEW_AFFILIATE_APP = 'Create New Affiliate Offer',
  NEW_SHOPIFY_PROMO = 'Create New Promo Offer',
  EDIT_OFFER = 'Sales Tracking',
  CONFIRMATION = 'Offer Confirmation',
  LANDINGPAGE = 'CreatorStore Settings',
}

const AffiliatesAppContent: React.FC<Readonly<IProps>> = (props) => {
  const history = useHistory();
  const {
    baseUri, clientId, clientName, children, dateRangeSettings, deletedMembers, selectedFilters, setSelectedFilters, selectedOfferType, setSelectedOfferType, selectedPrograms, setSelectedPrograms, isDrawerOpen, setIsDrawerOpen, isMemberTableFilterOpen, setIsMemberTableFilterOpen, selectedPayoutIds, setSelectedPayoutIds, selectedLastPayoutIds, setSelectedLastPayoutIds, numericFilters, setNumericFilters, selectedStatusFilters, setSelectedStatusFilters, selectedBoostTypes, setSelectedBoostTypes, selectedBoostStatuses, setSelectedBoostStatuses, offerNumericFilters, setOfferNumericFilters, offerSelectedStatusFilters, setOfferSelectedStatusFilters, boostData,
  } = props;
  const { earliestDate } = dateRangeSettings;
  const { refreshUi, archiveOffer, offerSimplification, refreshSTADashboard } = useClientFeatures();

  const location = useLocation();
  const { offerId } = useParams<IRouteParams>();

  // Extract offerId from URL path as fallback
  const offerIdFromUrl = getOfferIdFromUrl(location.pathname);
  const isDashboardPage = useMemo(() => location.pathname.indexOf('/dashboard') !== -1, [location.pathname]);
  const isOfferDetailsPage = useMemo(() => location.pathname.indexOf('/details') !== -1, [location.pathname]);
  const isOfferDetailsPageRef = useRef(isOfferDetailsPage);
  const isEditOfferPage = useMemo(() => location.pathname.indexOf('/edit') !== -1, [location.pathname]);
  const isOfferSettingsPage = useMemo(() => location.pathname.indexOf('/settings') !== -1, [location.pathname]);
  const isOfferSettingsAutomationPage = useMemo(() => location.pathname.indexOf('/offer_automation') !== -1, [location.pathname]);
  const newAffiliateLink = useMemo(() => location.pathname.indexOf('/newAffiliateLink') !== -1, [location.pathname]);
  const newShopifyPromo = useMemo(() => location.pathname.indexOf('/newShopifyPromo') !== -1, [location.pathname]);
  const confirmation = useMemo(() => location.pathname.indexOf('/confirmation') !== -1, [location.pathname]);
  const isLandingPage = useMemo(() => location.pathname.indexOf('/setupLandingPage') !== -1, [location.pathname]);
  const { offerFormValue, offer } = useOfferDetailsContext();
  const routeToExclude = isLandingPage || isOfferSettingsAutomationPage;
  const { handleBackWithConfirmation } = useSTAPaymentAppContext();
  // Use offerId from params or extract from URL
  const currentOfferId = offerId || offerIdFromUrl;
  useEffect(() => {
    isOfferDetailsPageRef.current = isOfferDetailsPage;
  }, [isOfferDetailsPage]);

  const offersDashboardHeader = [
    <DateFilter earliestDate={earliestDate} settings={dateRangeSettings} refreshUi={refreshUi} key={1} />,
    refreshSTADashboard && (
    <HeaderButton
      onClick={() => setIsDrawerOpen(true)}
      icon={<SlidersIcon className="h-4 w-4" />}
      key={2}
      tooltip="Filter"
    />
    ),
    <STAPaymentApp
      clientId={clientId}
      clientName={clientName}
      initialDateFilterSettings={dateRangeSettings}
      refreshUi={refreshUi}
      key={3}
    />,
  ];

  const goBack = useCallback(() => {
    if (isOfferDetailsPageRef.current) {
      history.push(`${baseUri}/dashboard`);
    } else {
      handleBackWithConfirmation(() => history.goBack());
    }
  }, [history, baseUri, handleBackWithConfirmation]);

  const getTitle = ({
    isDashboardPage,
    isEditOfferPage,
    newAffiliateLink,
    newShopifyPromo,
    confirmation,
  }) => {
    if (isDashboardPage) return STA_TITLE.DASHBOARD;
    if (newAffiliateLink) return offerFormValue?.name || STA_TITLE.NEW_AFFILIATE_APP;
    if (newShopifyPromo) return offerFormValue?.name || STA_TITLE.NEW_SHOPIFY_PROMO;
    if (isEditOfferPage) return offerFormValue?.name || STA_TITLE.EDIT_OFFER;
    if (confirmation) return STA_TITLE.CONFIRMATION;

    return 'Sales Tracking';
  };

  const aspirexApolloClient = useApolloClientForAspirex();
  const [programs, setPrograms] = React.useState<GetAllPrograms_programs[]>();

  React.useEffect(() => {
    aspirexApolloClient.query({
      query: GET_ALL_PROGRAMS,
    }).then((data) => {
      setPrograms(data.data.programs);
    }).catch((error) => {
      logger.error(error);
    });
  }, [aspirexApolloClient]);
  return (
    <Space direction="vertical" size={0} style={{ width: '100%', overflow: 'hidden' }}>
      {!routeToExclude && (
        <AppHeader
          title={getTitle({
            isDashboardPage,
            isEditOfferPage,
            newAffiliateLink,
            newShopifyPromo,
            confirmation,
          })}
          offerImage={offerSimplification ? (isEditOfferPage || newAffiliateLink || newShopifyPromo) ? <RefreshOfferDefaultImage className="!w-9 !h-9" imageUrl={offerFormValue?.imageUrl || null} source={offerFormValue?.source} icon={offerFormValue?.icon} iconClassName="h-4 w-4" /> : null : null}
          actions={isDashboardPage ? offersDashboardHeader : (offerSimplification && (isEditOfferPage || newAffiliateLink || newShopifyPromo)) ? [<NeedHelpDropdown key={1} source={offerFormValue?.source} />] : undefined}
          backButtonFunction={
            isEditOfferPage || newAffiliateLink || newShopifyPromo || confirmation
              ? goBack
              : undefined
          }
          customTitle={
            isOfferDetailsPage ? (
              <OfferDetailCard
                clientId={clientId}
                clientName={clientName}
                dateRangeSettings={dateRangeSettings}
                archiveOffer={archiveOffer}
                deletedMembers={deletedMembers || []}
                refreshUi={refreshUi}
                goBack={goBack}
                showCta
                setIsMemberTableFilterOpen={setIsMemberTableFilterOpen}
              />
            ) : (isOfferSettingsPage) ? (
              <OfferSettingsCustomTitle
                onBack={() => history.push(`${baseUri.replace('/dashboard', '')}/offers/${currentOfferId || offer?.id}/details`)}
                baseUri={baseUri}
                offerId={Number(currentOfferId || offer?.id)}
              />
            ) : undefined
          }
        />

      )}
      <Row gutter={0}>
        <Col xs={24}>
          {children}
        </Col>
      </Row>
      <RefreshMemberTableFilter
        open={isMemberTableFilterOpen}
        onOpenChange={setIsMemberTableFilterOpen}
        selectedPayoutIds={selectedPayoutIds}
        setSelectedPayoutIds={setSelectedPayoutIds}
        selectedLastPayoutIds={selectedLastPayoutIds}
        setSelectedLastPayoutIds={setSelectedLastPayoutIds}
        numericFilters={numericFilters}
        setNumericFilters={setNumericFilters}
        selectedStatusFilters={selectedStatusFilters}
        setSelectedStatusFilters={setSelectedStatusFilters}
        selectedBoostTypes={selectedBoostTypes}
        setSelectedBoostTypes={setSelectedBoostTypes}
        selectedBoostStatuses={selectedBoostStatuses}
        setSelectedBoostStatuses={setSelectedBoostStatuses}
        boostData={boostData}
      />
      <RefreshOfferFiltersDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        archiveOffer={archiveOffer}
        selectedOfferType={selectedOfferType}
        setSelectedOfferType={setSelectedOfferType}
        programs={programs}
        selectedPrograms={selectedPrograms}
        setSelectedPrograms={setSelectedPrograms}
        numericFilters={offerNumericFilters}
        setNumericFilters={setOfferNumericFilters}
        selectedStatusFilters={offerSelectedStatusFilters}
        setSelectedStatusFilters={setOfferSelectedStatusFilters}
      />
    </Space>
  );
};

export const AffiliatesApp: React.FC<Readonly<IProps>> = (props) => {
  return (
    <STAPaymentAppContextProvider>
      <AffiliatesAppContent {...props} />
    </STAPaymentAppContextProvider>
  );
};
